import { useCallback, useMemo, useRef } from 'react'

import {
  getAllAncestorsForItem,
  getAllDescendantsForItem,
  IDataShape,
} from 'cng-common/components/treeSelect'
import { PopupContent } from 'cng-common/components/treeSelect/components/popupContent/PopupContent'
import { SelectToggle } from 'cng-common/components/treeSelect/components/selectToggle/SelectToggle'
import { useEscapeKeyPress } from 'cng-common/components/treeSelect/hooks/useEscapeKeyPress'
import { usePopup } from 'cng-common/components/treeSelect/hooks/usePopup'
import {
  ITreeItemClickFn,
  ITreeSelectProps,
} from 'cng-common/components/treeSelect/models/treeSelectProps'
import styles from 'cng-common/components/treeSelect/TreeSelect.module.scss'
import { AbsolutePopup, TPopupVertical } from 'core-components/library/popup'

import { PREVENT_PARENT_AUTO_SELECT } from './configs'
import { useTreeSelect } from './hooks/useTreeSelect'

const EMPTY_EVENT_CALLBACKS = {}

/**
 * KeyObjectivesTreeSelect is a local copy of TreeSelect with a fixed selection initialization
 * behavior: when a parent ID is present in values, only the parent itself is highlighted,
 * not all of its children.
 */
export const KeyObjectivesTreeSelect = <T extends IDataShape>({
  values,
  data,
  itemCaption,
  label,
  popupClassName,
  onChange,
  onSearch,
  disabled,
  typeOfSelectedNodes = 'allSelectedItems',
}: ITreeSelectProps<T>) => {
  const { isOpened, setOpened } = usePopup()
  const listRef = useRef<HTMLUListElement>(null)

  const {
    treeData,
    flatData,
    searchValue,
    setSearchValue,
    showOnlySelected,
    setShowOnlySelected,
    selectedValues,
    selectItem,
    selectFromCompactParent,
    selectParentOnly,
    removeItem,
    removeAll,
  } = useTreeSelect({
    data,
    values,
    itemCaption,
    onChange,
    onSearch,
    typeOfSelectedNodes,
  })

  const handleItemClick = useCallback<ITreeItemClickFn>(
    (itemId) => {
      const directMatch = selectedValues.find((it) => it.itemId === itemId)
      const itemInFlat = flatData.find((it) => it.itemId === itemId)

      if (directMatch) {
        if (
          PREVENT_PARENT_AUTO_SELECT &&
          directMatch.checkedState === 'indeterminate' &&
          itemInFlat?.hasChildren
        ) {
          const allDescendants = getAllDescendantsForItem(itemInFlat).filter(
            (it) => it.itemId !== itemInFlat.itemId
          )
          const allChildrenSelected = allDescendants.every((desc) =>
            selectedValues.some((sv) => sv.itemId === desc.itemId && sv.checkedState === 'full')
          )

          if (allChildrenSelected) {
            selectParentOnly(itemId)
            return
          }
        }

        removeItem(itemId)
        return
      }

      const isImplicitlyChecked =
        itemInFlat !== undefined &&
        getAllAncestorsForItem(itemInFlat).some((ancestor) =>
          selectedValues.some((sv) => sv.itemId === ancestor.itemId && sv.checkedState === 'full')
        )

      if (isImplicitlyChecked) {
        selectFromCompactParent(itemId)
        return
      }

      selectItem(itemId)
    },
    [selectedValues, flatData, selectItem, selectFromCompactParent, selectParentOnly, removeItem]
  )

  const handleClosePopup = useCallback(() => {
    setOpened(false)
  }, [setOpened])

  const handlePopupVisibility = useCallback(
    (newValue: boolean) => {
      setOpened(newValue)
      return newValue
    },
    [setOpened]
  )

  const selectedValuesForToggle = useMemo(
    () => selectedValues.map((sv) => ({ ...sv, hasChildren: false })),
    [selectedValues]
  )

  useEscapeKeyPress({ cb: handleClosePopup, condition: isOpened })

  return (
    <div className={styles.root}>
      <AbsolutePopup
        popupClassName={popupClassName}
        verticalStartPosition={TPopupVertical.Bottom}
        visibilityCallback={handlePopupVisibility}
        eventCallbacks={EMPTY_EVENT_CALLBACKS}
        popupContent={() => {
          return (
            <PopupContent
              isOpened={isOpened}
              showOnlySelected={showOnlySelected}
              searchValue={searchValue}
              treeData={treeData}
              selectedValues={selectedValues}
              listRef={listRef}
              setSearchValue={setSearchValue}
              setShowOnlySelected={setShowOnlySelected}
              handleItemClick={handleItemClick}
              onBlur={handleClosePopup}
            />
          )
        }}
      >
        <SelectToggle
          label={label}
          selectedValues={selectedValuesForToggle}
          onRemoveItem={removeItem}
          onRemoveAll={removeAll}
          disabled={disabled}
          isOpened={isOpened}
        />
      </AbsolutePopup>
    </div>
  )
}
